import CustomCard from "components/Atoms/CustomCard"
import CustomTag from "components/Atoms/CustomTag"
import ReactTableHeader from "components/Common/ReactTableHeader"
import TablePagination from "components/Molecules/TablePagination"
import { useShipmentPlansQuery } from "generated/graphql"
import { useSearchFilter } from "hooks/searchFilterHooks"
import React from "react"
import ReactTable from "react-table"
import dayjs from "dayjs"
import SOModal from "./SOModal"
import UploadSalesOrderDialog from "./uploadSalesOrderDialog"
import SoActions from "../actions/SOActions"
import SearchFilter, {
  SearchFilterCallout,
} from "components/Organisms/SearchFilter"
import { useFilter } from "store/search-filter/search-filter.reducer"
import StaticSelector from "components/Atoms/StaticSelector"
import DynamicSelector from "components/Molecules/DynamicSelector"
import { EXPORT_CUSTOMER, VARIETIES } from "graphql/selector.queries.gql"
import CustomDateRange from "components/Atoms/CustomDateRange"
import { Icon } from "@blueprintjs/core"
const PRODUCTION_STATUSES = [
  { id: "NOT_CREATED", name: "Not Created" },
  { id: "CREATED", name: "Created" },
  { id: "SENT_FOR_APPROVAL", name: "Sent for Approval" },
  { id: "VERIFIED", name: "Verified" },
]

const SalesOrder = () => {
  const [{ page, search }, setFilter] = useSearchFilter({
    group: "*",
  })

  const [filterSalesOrder, setFilterSalesOrder] = useFilter("sales-order")

  const COLUMNS = [
    {
      Header: "Created On",
      accessor: "createdAt",
      Cell({ value, index }) {
        return (
          <div>
            <p>{dayjs(value).format("DD MMM 'YY")}</p>
            <p className="-mt-4 text-xs text-start">
              {dayjs(value).format("hh:mm A")}
            </p>
          </div>
        )
      },
    },
    {
      Header: "W Ref. No",
      accessor: "weeklySrNo",
      Cell: ({ value }) => {
        return <p className="text-center">{value}</p>
      },
    },
    {
      Header: "Sales Order",
      accessor: "soNo",
      Cell({ value }) {
        return <p className="text-center">{value ?? "--"}</p>
      },
    },
    {
      Header: "Customer name",
      accessor: "exportCustomer.name",
      Cell({ value }) {
        return <p className="text-center">{value}</p>
      },
    },
    {
      Header: "Variety",
      accessor: "varieties",
      Cell({ value, original }) {
        return (
          <div className="flex flex-col items-center">
            <p className="text-center mb-0">
              {value?.map((v: { name: string }) => v.name).join(", ")}
            </p>
            <CustomTag
              intent={original.varietyType === "MIX" ? "primary" : "warning"}
              round
              minimal
            >
              {original.varietyType}
            </CustomTag>
          </div>
        )
      },
    },
    {
      Header: "Packing Format (SKU)",
      accessor: "skuType.name",

      Cell({ value }) {
        return <p className="text-center">{value}</p>
      },
    },

    {
      Header: "MH Code",
      accessor: "approvedtradeprofileSet",
      Cell({ value, original }) {
        return (
          <div className="flex justify-center">
            <SOModal salesOrder={original} refetch={refetch} />
          </div>
        )
      },
    },
    {
      Header: "Production Status",
      accessor: "productionStatus",
      Cell({ value, original }) {
        const metadata = original?.production?.metadata
          ? JSON.parse(original?.production?.metadata)
          : null
        const status = metadata?.status

        const statusConfig: Record<string, { label: string; intent: string }> =
          {
            VERIFIED: { label: "Verified", intent: "success" },
            NOT_CREATED: { label: "Not Created", intent: "danger" },
            CREATED: { label: "Created", intent: "primary" },
            SENT_FOR_APPROVAL: {
              label: "Sent for Approval",
              intent: "warning",
            },
          }
        const cfg =
          status === "verify"
            ? statusConfig["VERIFIED"]
            : statusConfig[value] ?? {
                label: value ?? "--",
                intent: "none",
              }

        return (
          <div className="flex flex-col items-center gap-2 py-1 w-full px-1">
            {/* Status badge */}
            <CustomTag intent={cfg.intent as any} round minimal>
              {cfg.label}
            </CustomTag>
          </div>
        )
      },
    },
    {
      Header: "Pallets/Boxes",
      accessor: "productionStatus",
      Cell({ value, original }) {
        const productionItems: any[] =
          original?.production?.productionitemSet ?? []
        const totalPallets: number = original?.skuFilled ?? 0
        const gpAssignedPallets = productionItems.filter(
          (item: any) => (item?.linepackingallotmentSet?.length ?? 0) > 0
        ).length

        // ── Filled boxes / required boxes ───────────────────────
        const soMeta = original?.metadata
          ? JSON.parse(original?.metadata)
          : null
        const isEuro: boolean = soMeta?.is_euro ?? false
        const euroBoxes: number = soMeta?.euro_pallet_box_count ?? 0
        const regularBoxes: number = soMeta?.regular_pallet_box_count ?? 0
        const skuFilled: number = original?.skuFilled ?? 0
        let requiredBoxes = 0
        if (skuFilled > 0) {
          requiredBoxes = isEuro
            ? euroBoxes + (skuFilled - 1) * regularBoxes
            : skuFilled * regularBoxes
        }
        const filledBoxes = productionItems.reduce(
          (sum: number, item: any) => sum + (item?.boxCount ?? 0),
          0
        )

        const palletPct =
          totalPallets > 0
            ? Math.min(
                100,
                Math.round((gpAssignedPallets / totalPallets) * 100)
              )
            : 0
        const boxPct =
          requiredBoxes > 0
            ? Math.min(100, Math.round((filledBoxes / requiredBoxes) * 100))
            : 0

        return (
          <div className="flex justify-center items-center gap-2 py-1 w-full px-1">
            {/* Mini progress rows */}
            {(totalPallets > 0 || requiredBoxes > 0) && (
              <div
                style={{
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  padding: "6px 8px",
                  width: "80%",
                  minWidth: 150,
                  display: "flex",
                  flexDirection: "column",
                  gap: 5,
                }}
              >
                {/* Pallets row */}
                {totalPallets > 0 && (
                  <div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 2,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 10,
                          color: "#64748b",
                          fontWeight: 600,
                          letterSpacing: 0.2,
                        }}
                      >
                        <Icon icon="cargo-ship" color="#3b82f6" /> Pallets
                      </span>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: palletPct >= 100 ? "#16a34a" : "#3b82f6",
                        }}
                      >
                        {gpAssignedPallets}/{totalPallets}
                      </span>
                    </div>
                    <div
                      style={{
                        background: "#e2e8f0",
                        borderRadius: 99,
                        height: 4,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${palletPct}%`,
                          height: "100%",
                          borderRadius: 99,
                          background: palletPct >= 100 ? "#22c55e" : "#3b82f6",
                          transition: "width 0.4s ease",
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Boxes row */}
                {requiredBoxes > 0 && (
                  <div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 2,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 10,
                          color: "#64748b",
                          fontWeight: 600,
                          letterSpacing: 0.2,
                        }}
                      >
                        📦 Boxes
                      </span>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: boxPct >= 100 ? "#16a34a" : "#64748b",
                        }}
                      >
                        {filledBoxes}/{requiredBoxes}
                      </span>
                    </div>
                    <div
                      style={{
                        background: "#e2e8f0",
                        borderRadius: 99,
                        height: 4,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${boxPct}%`,
                          height: "100%",
                          borderRadius: 99,
                          background: boxPct >= 100 ? "#22c55e" : "#94a3b8",
                          transition: "width 0.4s ease",
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )
      },
    },
    {
      Header: "Actions",
      accessor: "actions",
      Cell({ value, original }) {
        return (
          <div className="flex justify-center">
            {" "}
            <SoActions soId={original?.id} refetch={refetch} />
          </div>
        )
      },
    },
  ]

  const {
    data: sData,
    refetch,
    loading,
    error,
  } = useShipmentPlansQuery({
    variables: {
      soNoContains: search ?? undefined,
      limit: 10,
      offset: page,
      varietyId: filterSalesOrder?.variety?.id ?? undefined,
      customerId: filterSalesOrder?.exportCustomer?.id ?? undefined,
      fromDate: filterSalesOrder?.productionCreatedRange?.[0]
        ? dayjs(filterSalesOrder.productionCreatedRange[0]).format("YYYY-MM-DD")
        : undefined,
      toDate: filterSalesOrder?.productionCreatedRange?.[1]
        ? dayjs(filterSalesOrder.productionCreatedRange[1]).format("YYYY-MM-DD")
        : undefined,
      productionStatus: filterSalesOrder?.productionStatus?.id ?? undefined,
    },
    fetchPolicy: "network-only",
  })
  const list = sData?.shipmentPlans?.results ?? []
  if (error) return <>{`Errors ${error.message}`}</>

  return (
    <CustomCard className="p-0">
      <ReactTableHeader
        icon="control"
        title="Sales Order"
        value={search ?? ""}
        searchPlaceHolder="Search by So No, W Ref. No, Customer"
        onQueryChange={search => {
          setFilter(prev => ({
            ...prev,
            search,
            page: 0,
          }))
        }}
        filter={
          <SearchFilter
            value={filterSalesOrder}
            className="ml-3"
            getValues={setFilterSalesOrder}
            onClear={() => setFilterSalesOrder({})}
          >
            <SearchFilterCallout title="Variety" fill>
              <DynamicSelector
                fill
                big
                formClassName="col-span-2"
                query={VARIETIES}
                variables={{
                  includeCommodity: false,
                }}
                path="variety"
                value={filterSalesOrder?.variety}
                onChange={(_path, value) => {
                  setFilterSalesOrder(prev => ({
                    ...prev,
                    variety: value,
                  }))
                }}
              />
            </SearchFilterCallout>
            <SearchFilterCallout title="Customer" fill>
              <DynamicSelector
                fill
                big
                formClassName="col-span-2"
                query={EXPORT_CUSTOMER}
                variables={{
                  includeCommodity: false,
                }}
                path="exportCustomer"
                value={filterSalesOrder?.exportCustomer}
                onChange={(_path, value) => {
                  setFilterSalesOrder(prev => ({
                    ...prev,
                    exportCustomer: value,
                  }))
                }}
              />
            </SearchFilterCallout>
            <SearchFilterCallout title="Production Status" fill>
              <StaticSelector
                fill
                big
                nullable
                items={PRODUCTION_STATUSES}
                path="productionStatus"
                value={filterSalesOrder?.productionStatus}
                renderText={item => item?.name ?? "Select Status"}
                onChange={(_path, value) => {
                  setFilterSalesOrder(prev => ({
                    ...prev,
                    productionStatus: value?.id ? value : undefined,
                  }))
                }}
              />
            </SearchFilterCallout>
            <SearchFilterCallout title="Created At" fill>
              <CustomDateRange
                path="productionCreatedRange"
                big={false}
                shortcuts={true}
                contiguousCalendarMonths={false}
                value={filterSalesOrder?.productionCreatedRange}
                onChange={(_path, value) => {
                  setFilterSalesOrder(prev => ({
                    ...prev,
                    productionCreatedRange: value,
                  }))
                }}
              />
            </SearchFilterCallout>
          </SearchFilter>
        }
      >
        <TablePagination
          disabledNext={sData?.shipmentPlans?.results.length < 10}
          className="mx-3"
          page={page}
          onPageChange={page => {
            setFilter(prev => ({
              ...prev,
              page,
            }))
          }}
        />

        <UploadSalesOrderDialog refetch={refetch} />
      </ReactTableHeader>

      <ReactTable
        className="mt-0 vt-master-table vt_user_table"
        loading={loading}
        pageSize={10}
        showPagination={false}
        resizable={true}
        data={list}
        columns={COLUMNS}
      />
    </CustomCard>
  )
}
export default SalesOrder
